import React, { useState, useRef, useEffect } from 'react';
import { Distributor } from '../../types';
import './DistributorSelector.css';

interface DistributorSelectorProps {
  distributors: Distributor[];
  selectedDistributor: Distributor | null;
  onChange: (distributor: Distributor | null) => void;
  placeholder?: string;
}

const DistributorSelector: React.FC<DistributorSelectorProps> = ({
  distributors,
  selectedDistributor,
  onChange,
  placeholder = 'Select Distributor'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredDistributors = distributors.filter(distributor =>
    distributor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    distributor.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    distributor.state.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (distributor: Distributor | null) => {
    onChange(distributor);
    setIsOpen(false);
    setSearchTerm('');
  };

  const displayText = selectedDistributor 
    ? `${selectedDistributor.name} - ${selectedDistributor.city}, ${selectedDistributor.state}`
    : placeholder;

  return (
    <div className="distributor-selector" ref={dropdownRef}>
      <div 
        className="distributor-selector-display" 
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={selectedDistributor ? 'selected' : 'placeholder'}>
          {displayText}
        </span>
        <svg 
          className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
          width="12" 
          height="8" 
          viewBox="0 0 12 8" 
          fill="none"
        >
          <path 
            d="M1 1.5L6 6.5L11 1.5" 
            stroke="#616161" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="distributor-selector-dropdown">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search distributors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          
          <div className="distributor-list">
            {selectedDistributor && (
              <div 
                className="distributor-item clear-selection"
                onClick={() => handleSelect(null)}
              >
                <span>All Distributors</span>
              </div>
            )}
            
            {filteredDistributors.length > 0 ? (
              filteredDistributors.map((distributor) => (
                <div
                  key={distributor.distributor_id}
                  className={`distributor-item ${
                    selectedDistributor?.distributor_id === distributor.distributor_id ? 'selected' : ''
                  }`}
                  onClick={() => handleSelect(distributor)}
                >
                  <div className="distributor-name">{distributor.name}</div>
                  <div className="distributor-location">
                    {distributor.city}, {distributor.state} {distributor.zip}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">No distributors found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DistributorSelector;
